import '../styles/workspace-chrome.css';

import type { Event, NodeResponse, WorkspaceResponse } from '@simple-agent-manager/shared';
import { Alert, Button, PageLayout, Skeleton } from '@simple-agent-manager/ui';
import { Rocket } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { DockerSection } from '../components/node/DockerSection';
import { LogsSection } from '../components/node/LogsSection';
import { NodeEventsSection } from '../components/node/NodeEventsSection';
import { NodeOverviewSection } from '../components/node/NodeOverviewSection';
import { NodeWorkspacesSection } from '../components/node/NodeWorkspacesSection';
import { SoftwareSection } from '../components/node/SoftwareSection';
import { SystemResourcesSection } from '../components/node/SystemResourcesSection';
import { useNodeSystemInfo } from '../hooks/useNodeSystemInfo';
import { useProviderCatalog } from '../hooks/useProviderCatalog';
import { useToast } from '../hooks/useToast';
import {
  deleteNode,
  deleteWorkspace,
  getNode,
  listNodeEvents,
  listWorkspaces,
  restartWorkspace,
  stopNode,
  stopWorkspace,
} from '../lib/api';

export function Node() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const { catalogs } = useProviderCatalog();
  const [node, setNode] = useState<NodeResponse | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceResponse[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [stopping, setStopping] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { systemInfo, loading: sysInfoLoading } = useNodeSystemInfo(id, node?.status);
  const isDeploymentNode = node?.nodeRole === 'deployment';
  const deploymentEnvironments = node?.deploymentEnvironments ?? [];

  const loadNode = useCallback(async () => {
    if (!id) return;

    try {
      setError(null);
      const [nodeResponse, workspaceResponse] = await Promise.all([
        getNode(id),
        listWorkspaces(undefined, id),
      ]);
      setNode(nodeResponse);
      setWorkspaces(workspaceResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load node');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadNode();
    const interval = window.setInterval(() => void loadNode(), 10000);
    return () => window.clearInterval(interval);
  }, [loadNode]);

  // Fetch node events via control plane proxy (vm-* DNS records lack SSL termination)
  useEffect(() => {
    if (!id || !node || node.status !== 'running') return;

    const fetchEvents = async () => {
      try {
        const data = await listNodeEvents(id, 50);
        setEvents(data.events || []);
        setEventsError(null);
      } catch (err) {
        setEventsError(err instanceof Error ? err.message : 'Failed to load events');
      }
    };

    void fetchEvents();
    const interval = window.setInterval(() => void fetchEvents(), 10000);
    return () => window.clearInterval(interval);
  }, [id, node?.status]);

  const handleStop = async () => {
    if (!id || !node) return;

    const confirmed = window.confirm(
      `Stop node "${node.name}"? This stops all workspaces and agent sessions on the node.`
    );
    if (!confirmed) return;

    const prevNode = node;
    const prevWorkspaces = workspaces;
    setNode({ ...node, status: 'stopping' });
    setWorkspaces((ws) =>
      ws.map((w) =>
        w.status === 'running' || w.status === 'recovery'
          ? { ...w, status: 'stopping' as const }
          : w
      )
    );
    setStopping(true);
    try {
      await stopNode(id);
      toast.success('Node stopping');
    } catch (err) {
      setNode(prevNode);
      setWorkspaces(prevWorkspaces);
      toast.error(err instanceof Error ? err.message : 'Failed to stop node');
    } finally {
      setStopping(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !node) return;

    if (isDeploymentNode) {
      const envSummary = deploymentEnvironments.length > 0
        ? `${deploymentEnvironments.length} deployment environment${deploymentEnvironments.length === 1 ? '' : 's'}: ${deploymentEnvironments.map((env) => env.name).join(', ')}`
        : 'deployment environments currently listed on this node';
      const confirmed = window.confirm(
        `This is a deployment node ("${node.name}") hosting ${envSummary}. Deleting it here destroys the node infrastructure and affects ALL hosted environments, but it does not perform each environment's volume teardown.\n\nFor full per-environment teardown, use Destroy on the project Deployments page.\n\nContinue with node-only deletion?`
      );
      if (!confirmed) return;
    } else {
      const confirmed = window.confirm(
        `Delete node "${node.name}"? This permanently deletes the node and all attached workspaces/sessions.`
      );
      if (!confirmed) return;
    }

    try {
      setDeleting(true);
      await deleteNode(id);
      toast.success('Node deleted');
      navigate('/nodes');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete node');
      setDeleting(false);
    }
  };

  const handleRetryEvents = () => {
    if (!id) return;
    setEventsError(null);
    void listNodeEvents(id, 50).then((data) => {
      setEvents(data.events || []);
    }).catch((err) => {
      setEventsError(err instanceof Error ? err.message : 'Failed to load events');
    });
  };

  const handleStopWorkspace = async (workspaceId: string) => {
    try {
      setWorkspaces((ws) =>
        ws.map((w) => (w.id === workspaceId ? { ...w, status: 'stopping' as const } : w))
      );
      await stopWorkspace(workspaceId);
      toast.success('Workspace stopping');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to stop workspace');
      void loadNode();
    }
  };

  const handleRestartWorkspace = async (workspaceId: string) => {
    try {
      setWorkspaces((ws) =>
        ws.map((w) => (w.id === workspaceId ? { ...w, status: 'creating' as const } : w))
      );
      await restartWorkspace(workspaceId);
      toast.success('Workspace restarting');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to restart workspace');
      void loadNode();
    }
  };

  const handleDeleteWorkspace = async (workspaceId: string) => {
    const ws = workspaces.find((w) => w.id === workspaceId);
    const confirmed = window.confirm(
      `Delete workspace "${ws?.displayName || ws?.name || workspaceId}"? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await deleteWorkspace(workspaceId);
      setWorkspaces((prev) => prev.filter((w) => w.id !== workspaceId));
      toast.success('Workspace deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete workspace');
    }
  };

  return (
    <PageLayout title="Node" maxWidth="xl">
      {/* Breadcrumb navigation */}
      <nav className="flex items-center gap-2 mb-4 text-fg-muted" style={{ fontSize: 'var(--sam-type-secondary-size)' }}>
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-transparent border-none text-accent cursor-pointer p-0"
          style={{ fontSize: 'inherit' }}
        >
          Dashboard
        </button>
        <span>/</span>
        <button
          onClick={() => navigate('/nodes')}
          className="bg-transparent border-none text-accent cursor-pointer p-0"
          style={{ fontSize: 'inherit' }}
        >
          Nodes
        </button>
        <span>/</span>
        <span className="text-fg-primary">{node?.name || 'Loading...'}</span>
      </nav>

      {/* Action buttons */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        {!isDeploymentNode && (
          <Button onClick={() => navigate('/workspaces/new', { state: id ? { nodeId: id } : undefined })}>
            Create Workspace
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={handleStop}
          disabled={stopping || deleting || !node || node.status === 'stopped'}
        >
          {stopping ? 'Stopping...' : 'Stop Node'}
        </Button>
        <Button
          variant="danger"
          onClick={handleDelete}
          disabled={stopping || deleting || !node}
        >
          {deleting ? 'Deleting...' : isDeploymentNode ? 'Delete Node Only' : 'Delete Node'}
        </Button>
      </div>

      {error && (
        <div className="mb-4">
          <Alert variant="error" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {loading && !node ? (
        <div className="flex flex-col gap-6">
          <div
            aria-hidden="true"
            className="border border-border-default rounded-lg p-6 bg-surface grid gap-4"
          >
            <div className="flex justify-between items-center">
              <Skeleton width="40%" height="1.25rem" />
              <div className="flex gap-2">
                <Skeleton width="60px" height="1.25rem" borderRadius="9999px" />
                <Skeleton width="60px" height="1.25rem" borderRadius="9999px" />
              </div>
            </div>
            <div className="border-t border-border-default pt-4 grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i}>
                  <Skeleton width="50%" height="0.75rem" style={{ marginBottom: 'var(--sam-space-1)' }} />
                  <Skeleton width="70%" height="0.875rem" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : !node ? (
        <Alert variant="error">Node not found</Alert>
      ) : (
        <div className="flex flex-col gap-6">
          {isDeploymentNode && (
            <div className="glass-surface rounded-md p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-sm bg-accent-tint flex items-center justify-center shrink-0">
                <Rocket size={18} className="text-accent" />
              </div>
              <div className="min-w-0">
                <h2 className="m-0 text-sm font-semibold text-fg-primary">Deployment node</h2>
                <p className="m-0 mt-1 text-sm text-fg-muted">
                  This node runs {deploymentEnvironments.length || 'one or more'} project deployment environment{deploymentEnvironments.length === 1 ? '' : 's'}. Logs and system health are available here; environment policy, DNS cleanup, volume teardown, and release management are on the project Deployments page.
                </p>
                <p className="m-0 mt-1 text-sm text-fg-muted">
                  {deploymentEnvironments.length > 0
                    ? `Hosted environments: ${deploymentEnvironments.map((env) => env.name).join(', ')}. `
                    : ''}
                  Deleting the node directly affects every hosted environment; use <strong>Destroy</strong> on the Deployments page for per-environment teardown.
                </p>
              </div>
            </div>
          )}

          <NodeOverviewSection node={node} systemInfo={systemInfo} catalogs={catalogs} />

          {node.status === 'running' && (
            <>
              <SystemResourcesSection
                systemInfo={systemInfo}
                fallbackMetrics={node.lastMetrics}
                loading={sysInfoLoading}
              />
              <DockerSection docker={systemInfo?.docker} loading={sysInfoLoading} />
              <SoftwareSection
                software={systemInfo?.software}
                agent={systemInfo?.agent}
                loading={sysInfoLoading}
              />
            </>
          )}

          <LogsSection nodeId={id} nodeStatus={node.status} />

          {!isDeploymentNode && (
            <NodeWorkspacesSection
              workspaces={workspaces}
              onCreateWorkspace={() => navigate('/workspaces/new', { state: id ? { nodeId: id } : undefined })}
              onStop={handleStopWorkspace}
              onRestart={handleRestartWorkspace}
              onDelete={handleDeleteWorkspace}
            />
          )}

          <NodeEventsSection
            events={events}
            error={eventsError}
            onRetry={handleRetryEvents}
            nodeStatus={node.status}
            nodeId={node.id}
          />
        </div>
      )}
    </PageLayout>
  );
}
